<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import EnterpriseAccountAPI from 'dashboard/api/enterprise/account';

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const store = useStore();
const { localeTag } = useDateLocale();

const dialogRef = ref(null);
const addons = ref([]);
const selectedPriceId = ref(null);
const isLoading = ref(false);
const isFetching = ref(false);

const selectedAddon = computed(() =>
  addons.value.find(a => a.price_id === selectedPriceId.value)
);

const formatPrice = addon =>
  new Intl.NumberFormat(localeTag.value, {
    style: 'currency',
    currency: (addon.currency || 'brl').toUpperCase(),
  }).format(addon.amount);

const addonLabel = addon => {
  if (addon.type === 'cam') {
    return t('BILLING_SETTINGS.ADDONS.CAM_PACK', {
      count: addon.unit.toLocaleString(localeTag.value),
    });
  }
  if (addon.type === 'channel') {
    return t('BILLING_SETTINGS.ADDONS.CHANNEL', { count: addon.unit });
  }
  return t('BILLING_SETTINGS.ADDONS.SEAT', { count: addon.unit });
};

const fetchAddons = async () => {
  isFetching.value = true;
  try {
    const { data } = await EnterpriseAccountAPI.getAddonPrices();
    addons.value = data.addons || [];
    selectedPriceId.value = addons.value[0]?.price_id || null;
  } catch (error) {
    useAlert(t('BILLING_SETTINGS.ADDONS.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

const open = () => {
  addons.value = [];
  selectedPriceId.value = null;
  isLoading.value = false;
  dialogRef.value?.open();
  fetchAddons();
};

const close = () => dialogRef.value?.close();

const handlePurchase = async () => {
  if (!selectedAddon.value) return;
  isLoading.value = true;
  try {
    const { data } = await EnterpriseAccountAPI.createAddonCheckout(
      selectedAddon.value.price_id,
      1
    );
    close();
    emit('success', data);
    store.dispatch('accounts/limits');
    useAlert(t('BILLING_SETTINGS.ADDONS.PURCHASE_SUCCESS'));
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('BILLING_SETTINGS.ADDONS.PURCHASE_ERROR')
    );
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('BILLING_SETTINGS.ADDONS.MODAL_TITLE')"
    :description="t('BILLING_SETTINGS.ADDONS.MODAL_DESCRIPTION')"
    width="md"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="emit('close')"
  >
    <div v-if="isFetching" class="flex justify-center py-10">
      <Spinner :size="28" class="text-n-brand" />
    </div>
    <div v-else class="flex flex-col gap-3">
      <button
        v-for="addon in addons"
        :key="addon.price_id"
        type="button"
        role="radio"
        :aria-checked="addon.price_id === selectedPriceId"
        class="flex items-center gap-3 px-4 py-3.5 rounded-xl border text-left transition-colors"
        :class="
          addon.price_id === selectedPriceId
            ? 'border-n-brand bg-n-brand/5 ring-1 ring-n-brand'
            : 'border-n-weak hover:border-n-slate-6'
        "
        @click="selectedPriceId = addon.price_id"
      >
        <span
          class="shrink-0 flex items-center justify-center w-4 h-4 rounded-full border-2"
          :class="
            addon.price_id === selectedPriceId
              ? 'border-n-brand'
              : 'border-n-slate-6'
          "
        >
          <span
            v-if="addon.price_id === selectedPriceId"
            class="w-2 h-2 rounded-full bg-n-brand"
          />
        </span>
        <span class="flex-1 font-medium text-n-slate-12">
          {{ addonLabel(addon) }}
        </span>
        <span class="tabular-nums text-sm font-medium text-n-slate-12">
          {{ `${formatPrice(addon)}${t('BILLING_SETTINGS.ADDONS.MONTH')}` }}
        </span>
      </button>

      <div class="flex justify-end gap-2 mt-4">
        <Button
          faded
          slate
          :label="t('BILLING_SETTINGS.ADDONS.CANCEL')"
          @click="close"
        />
        <Button
          solid
          blue
          :is-loading="isLoading"
          :disabled="!selectedAddon || isLoading"
          :label="t('BILLING_SETTINGS.ADDONS.CONFIRM')"
          @click="handlePurchase"
        />
      </div>
    </div>
  </Dialog>
</template>
